import { create } from '@bufbuild/protobuf'
import { timestampFromDate } from '@bufbuild/protobuf/wkt'
import { useMutation } from '@connectrpc/connect-query'
import { useUser } from '@descope/react-sdk'
import { useQueryClient } from '@tanstack/react-query'
import _ from 'lodash'

import { getControlQueryKey, getControlTimelineQueryKey } from '@/api/add-comment.hook'
import { getGetControlQueryKey, getListControlsQueryKey } from '@/api/control.hook'
import {
  ControlSchema,
  ControlTimelineItemSchema,
  GetControlResponse,
  GetControlResponseSchema,
  GetControlTimelineResponse,
  GetControlTimelineResponseSchema,
  RiskQuestionnaireControlThreadSchema,
  RiskQuestionnaireQuestionThreadActivity,
  RiskQuestionnaireQuestionThreadActivitySchema,
  SmartQuestionSchema,
  SmartQuestionStatus,
} from '@/gen/inventory/v1/control_service_pb'
import { QuestionnaireStatus } from '@/gen/questionnaire/common/v1/questionnaire_status_pb'
import { submitRiskQuestionnaireThreadActivity } from '@/gen/questionnaire/v2/questionnaire-QuestionnaireService_connectquery'
import { SubmitRiskQuestionnaireThreadActivityRequest } from '@/gen/questionnaire/v2/questionnaire_pb'

import { sortByTimestamp } from '@/lib/date/date'

export const useOptimisticSubmitRiskQuestionnaireThreadActivity = () => {
  const queryClient = useQueryClient()
  const { user } = useUser()

  return useMutation(submitRiskQuestionnaireThreadActivity, {
    onMutate: async (variables) => {
      const timelineQueryKey = getControlTimelineQueryKey(variables.controlStatusId)
      const controlQueryKey = getGetControlQueryKey(variables.controlStatusId)
      await Promise.all([
        queryClient.cancelQueries({ queryKey: timelineQueryKey }),
        queryClient.cancelQueries({ queryKey: controlQueryKey }),
      ])

      const previousTimeline =
        queryClient.getQueryData<GetControlTimelineResponse>(timelineQueryKey)
      const previousControl = queryClient.getQueryData<GetControlResponse>(controlQueryKey)

      const optimisticTimeline = previousTimeline
        ? appendActivityToLatestRiskThread(
            previousTimeline,
            variables as SubmitRiskQuestionnaireThreadActivityRequest,
            user?.userId,
          )
        : undefined
      if (optimisticTimeline) {
        queryClient.setQueryData(timelineQueryKey, optimisticTimeline)
      }

      // The Resolved banner is driven by wasMarkedAsResolved in the card.
      const shouldRecomputeStatus = Boolean(variables.comment) || variables.isResolved === false
      if (previousControl?.control?.smartQuestion && shouldRecomputeStatus) {
        const nextStatus = computeOptimisticStatus(
          optimisticTimeline,
          previousControl.control.smartQuestion.status,
        )
        queryClient.setQueryData(
          controlQueryKey,
          updateSmartQuestionStatus(previousControl, nextStatus),
        )
      }

      return { previousTimeline, previousControl, timelineQueryKey, controlQueryKey }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousTimeline) {
        queryClient.setQueryData(context.timelineQueryKey, context.previousTimeline)
      }
      if (context?.previousControl) {
        queryClient.setQueryData(context.controlQueryKey, context.previousControl)
      }
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: getControlTimelineQueryKey(variables.controlStatusId),
      })
      queryClient.invalidateQueries({ queryKey: getControlQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListControlsQueryKey() })
    },
  })
}

const computeOptimisticStatus = (
  timeline: GetControlTimelineResponse | undefined,
  fallback: SmartQuestionStatus,
): SmartQuestionStatus => {
  const activities = latestRiskThreadActivities(timeline)
  return activities ? computeStatusFromActivities(activities, fallback) : fallback
}

// Mirrors getStatusBasedOnThreadActivityFromRawControlStatus in
// services/internal/control/control.go. PUBLISHED / VALIDATED depend on control-status
// metadata outside the timeline, so we fall back and let the server refetch reconcile.
export const computeStatusFromActivities = (
  activities: RiskQuestionnaireQuestionThreadActivity[],
  fallback: SmartQuestionStatus,
): SmartQuestionStatus => {
  // Status changes only inform "submitted" — split them off so downstream lookups see
  // just authored activities (comments and resolutions).
  const [statusActivities, authoredActivities] = _.partition(
    activities,
    (a) => a.questionnaireStatus !== undefined,
  )

  const latestResolution = sortByTimestamp(
    authoredActivities.filter((a) => a.markedAsResolved !== undefined),
    (a) => a.createdAt,
  )[0]
  if (latestResolution?.markedAsResolved === true) {
    return SmartQuestionStatus.RESOLVED
  }

  // Reopen writes ONGOING after SUBMITTED, so the newest status activity is authoritative.
  const latestStatusActivity = sortByTimestamp(statusActivities, (a) => a.createdAt)[0]
  const submitted = latestStatusActivity?.questionnaireStatus === QuestionnaireStatus.SUBMITTED

  const latestSpeaker = sortByTimestamp(
    authoredActivities.filter((a) => a.markedAsResolved === undefined),
    (a) => a.createdAt,
  )[0]
  const isUserSpeaker = latestSpeaker?.userId?.case === 'assessorUserId'
  const isRecipientSpeaker = latestSpeaker?.userId?.case === 'recipientId'
  const hasComment = Boolean(latestSpeaker?.comment)

  if (submitted && isUserSpeaker && hasComment) return SmartQuestionStatus.AWAITING_REOPEN
  if (submitted) return SmartQuestionStatus.AWAITING_REVIEW
  if (isRecipientSpeaker) return SmartQuestionStatus.AWAITING_REVIEW
  if (isUserSpeaker) return SmartQuestionStatus.AWAITING_RECIPIENT
  return fallback
}

const latestRiskThreadActivities = (
  timeline: GetControlTimelineResponse | undefined,
): RiskQuestionnaireQuestionThreadActivity[] | undefined => {
  const latestThread = timeline?.items.find(
    (item) => item.timelineItem.case === 'riskQuestionnaireControlThread',
  )
  if (latestThread?.timelineItem.case !== 'riskQuestionnaireControlThread') return undefined
  return latestThread.timelineItem.value.activities
}

const updateSmartQuestionStatus = (
  oldData: GetControlResponse,
  status: SmartQuestionStatus,
): GetControlResponse => {
  if (!oldData.control?.smartQuestion) {
    return oldData
  }
  return create(GetControlResponseSchema, {
    ...oldData,
    control: create(ControlSchema, {
      ...oldData.control,
      smartQuestion: create(SmartQuestionSchema, {
        ...oldData.control.smartQuestion,
        status,
      }),
    }),
  })
}

const appendActivityToLatestRiskThread = (
  oldData: GetControlTimelineResponse,
  variables: SubmitRiskQuestionnaireThreadActivityRequest,
  currentUserId: string | undefined,
): GetControlTimelineResponse => {
  const latestThreadIndex = oldData.items.findIndex(
    (item) => item.timelineItem.case === 'riskQuestionnaireControlThread',
  )
  if (latestThreadIndex === -1) {
    return oldData
  }

  const optimisticActivity = create(RiskQuestionnaireQuestionThreadActivitySchema, {
    id: `optimistic-${Date.now()}`,
    userId: currentUserId ? { case: 'assessorUserId', value: currentUserId } : { case: undefined },
    comment: variables.comment || undefined,
    markedAsResolved: variables.isResolved,
    liveblocksThreadId: variables.liveblocksThreadId,
    userName: '',
    createdAt: timestampFromDate(new Date()),
  })

  const updatedItems = oldData.items.map((item, index) => {
    if (
      index !== latestThreadIndex ||
      item.timelineItem.case !== 'riskQuestionnaireControlThread'
    ) {
      return item
    }
    const thread = item.timelineItem.value
    return create(ControlTimelineItemSchema, {
      timelineItem: {
        case: 'riskQuestionnaireControlThread',
        value: create(RiskQuestionnaireControlThreadSchema, {
          ...thread,
          activities: [...thread.activities, optimisticActivity],
        }),
      },
    })
  })

  return create(GetControlTimelineResponseSchema, {
    ...oldData,
    items: updatedItems,
  })
}
